<script setup>
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import { ref, computed, markRaw, onMounted, watch } from 'vue';
import { MarkerType } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import FlowCanvas from './FlowCanvas.vue';
import { nodeActionSignal } from './nodeEventBus';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import FlowsAPI from 'dashboard/api/flows';
import FlowSidebar from './FlowSidebar.vue';
import FlowNodeEditor from './FlowNodeEditor.vue';
import TriggerNode from './components/TriggerNode.vue';
import MessageNode from './components/MessageNode.vue';
import BlockNode from './components/BlockNode.vue';
import ConditionNode from './components/ConditionNode.vue';
import ActionNode from './components/ActionNode.vue';
import DataCollectionNode from './components/DataCollectionNode.vue';
import GroupNode from './components/GroupNode.vue';
import DelayNode from './components/DelayNode.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const accountId = computed(() => getters.getCurrentAccountId.value);
const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);

const nodes = ref([]);
const edges = ref([]);

const flowName = ref('');
const flowDescription = ref('');
const flowId = ref(null);
const selectedNode = ref(null);
const saving = ref(false);
const loading = ref(false);
const showSidebar = ref(false);
const showSaveModal = ref(false);
const showEditNameModal = ref(false);
const showExitConfirmModal = ref(false);
const editName = ref('');
const editDescription = ref('');
const flowStatus = ref('draft');
const respectBusinessHours = ref(false);
const businessHoursStart = ref('09:00');
const businessHoursEnd = ref('18:00');
const editRespectBusinessHours = ref(false);
const editBusinessHoursStart = ref('09:00');
const editBusinessHoursEnd = ref('18:00');
const savedSnapshot = ref(null);

const isEditMode = computed(() => flowId.value !== null);

const cleanNodes = rawNodes =>
  (rawNodes || []).map(node => {
    const cleaned = {
      id: node.id,
      type: node.type,
      position: node.position,
      data: node.data || {},
    };
    if (node.parentNode) cleaned.parentNode = node.parentNode;
    if (node.extent) cleaned.extent = node.extent;
    if (node.style) cleaned.style = node.style;
    return cleaned;
  });

const cleanEdges = rawEdges =>
  (rawEdges || []).map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    type: 'bezier',
    markerEnd: MarkerType.ArrowClosed,
    style: { strokeWidth: 1 },
  }));

const hasUnsavedChanges = computed(() => {
  if (!savedSnapshot.value) return true;
  const current = JSON.stringify({
    name: flowName.value,
    description: flowDescription.value,
    respectBusinessHours: respectBusinessHours.value,
    businessHoursStart: businessHoursStart.value,
    businessHoursEnd: businessHoursEnd.value,
    nodes: cleanNodes(nodes.value),
    edges: cleanEdges(edges.value),
  });
  return current !== savedSnapshot.value;
});

const statusBadge = computed(() => {
  if (saving.value)
    return {
      label: t('FLOW_BUILDER.EDITOR.STATUS.SAVING'),
      class: 'bg-n-amber-9/10 text-n-amber-11',
    };
  if (hasUnsavedChanges.value)
    return {
      label: t('FLOW_BUILDER.EDITOR.STATUS.UNSAVED'),
      class: 'bg-n-amber-9/10 text-n-amber-11',
    };
  if (flowStatus.value === 'active')
    return {
      label: t('FLOW_BUILDER.EDITOR.STATUS.ACTIVE'),
      class: 'bg-n-green-9/10 text-n-green-11',
    };
  if (flowStatus.value === 'paused')
    return {
      label: t('FLOW_BUILDER.EDITOR.STATUS.PAUSED'),
      class: 'bg-n-ruby-9/10 text-n-ruby-11',
    };
  return {
    label: t('FLOW_BUILDER.EDITOR.STATUS.DRAFT'),
    class: 'bg-n-slate-9/10 text-n-slate-11',
  };
});

const takeSnapshot = () => {
  savedSnapshot.value = JSON.stringify({
    name: flowName.value,
    description: flowDescription.value,
    respectBusinessHours: respectBusinessHours.value,
    businessHoursStart: businessHoursStart.value,
    businessHoursEnd: businessHoursEnd.value,
    nodes: cleanNodes(nodes.value),
    edges: cleanEdges(edges.value),
  });
};

const nodeTypes = {
  trigger: markRaw(TriggerNode),
  message: markRaw(MessageNode),
  block: markRaw(BlockNode),
  data_collection: markRaw(DataCollectionNode),
  condition: markRaw(ConditionNode),
  action: markRaw(ActionNode),
  group: markRaw(GroupNode),
  delay: markRaw(DelayNode),
};

const defaultNodeData = {
  group: { label: 'Group', description: '', color: 'slate' },
  delay: { delay_value: 1, delay_unit: 'seconds', label: 'Delay' },
  trigger: {
    event_name: 'conversation_created',
    inbox_id: null,
    label: 'Trigger',
  },
  message: { content: '', label: 'Message' },
  block: { content_items: [], label: 'Block' },
  condition: { conditions: [], condition_match: 'all', label: 'Condition' },
  action: { actions: [], label: 'Action' },
  data_collection: {
    prompt_message: '',
    response_type: 'text',
    attribute_key: '',
    attribute_display_name: '',
    timeout_value: 5,
    timeout_unit: 'minutes',
    label: 'Data collection',
  },
};

const onCanvasDrop = ({ type, position }) => {
  if (!type || !defaultNodeData[type]) return;
  const newNode = {
    id: `${type}_${Date.now()}`,
    type,
    position,
    data: { ...defaultNodeData[type] },
  };
  if (type === 'group') {
    newNode.style = { width: '400px', height: '250px' };
  }
  nodes.value = [...nodes.value, newNode];
};

const onCanvasConnect = connection => {
  edges.value = [
    ...edges.value,
    {
      ...connection,
      id: `e_${connection.source}_${connection.target}_${Date.now()}`,
      type: 'bezier',
      markerEnd: MarkerType.ArrowClosed,
      style: { strokeWidth: 1 },
    },
  ];
};

const onCanvasEdgeClick = edge => {
  edges.value = edges.value.filter(e => e.id !== edge.id);
};

const onCanvasNodeClick = ({ node }) => {
  if (node.type === 'group') return;
  selectedNode.value = node;
};

const onCanvasPaneClick = () => {
  selectedNode.value = null;
  showSidebar.value = false;
};

const onCanvasNodeDragStop = ({
  node,
  groupId,
  relativePosition,
  absolutePosition,
}) => {
  if (!node || node.type === 'group') return;

  const idx = nodes.value.findIndex(n => n.id === node.id);
  if (idx === -1) return;

  if (groupId) {
    nodes.value[idx].parentNode = groupId;
    if (relativePosition) {
      nodes.value[idx].position = { ...relativePosition };
    }
  } else if (groupId === null) {
    delete nodes.value[idx].parentNode;
    if (absolutePosition) {
      nodes.value[idx].position = { ...absolutePosition };
    }
  }
};

const updateNodeData = (nodeId, newData) => {
  const node = nodes.value.find(n => n.id === nodeId);
  if (!node) return;
  if (newData.resize) {
    node.style = { ...(node.style || {}), ...newData.resize };
    return;
  }
  node.data = { ...node.data, ...newData };
};

const deleteNode = nodeId => {
  const node = nodes.value.find(n => n.id === nodeId);
  if (node && node.type === 'group') {
    nodes.value = nodes.value.map(n => {
      if (n.parentNode !== nodeId) return n;
      const { parentNode, extent, ...rest } = n;
      return {
        ...rest,
        position: {
          x: (node.position.x || 0) + (n.position?.x || 0),
          y: (node.position.y || 0) + (n.position?.y || 0),
        },
      };
    });
  }
  nodes.value = nodes.value.filter(n => n.id !== nodeId);
  edges.value = edges.value.filter(
    e => e.source !== nodeId && e.target !== nodeId
  );
  selectedNode.value = null;
};

const duplicateNode = nodeId => {
  const node = nodes.value.find(n => n.id === nodeId);
  if (!node) return;
  const newId = `${node.type}_${Date.now()}`;
  const newNode = {
    id: newId,
    type: node.type,
    position: { x: node.position.x + 40, y: node.position.y + 40 },
    data: JSON.parse(JSON.stringify(node.data)),
  };
  nodes.value = [...nodes.value, newNode];
};

watch(nodeActionSignal, ({ action, nodeId, ts, data }) => {
  if (!ts || !nodeId) return;
  if (action === 'delete') deleteNode(nodeId);
  else if (action === 'duplicate') duplicateNode(nodeId);
  else if (action === 'update') updateNodeData(nodeId, data || {});
});

const openEditNameModal = () => {
  editName.value = flowName.value;
  editDescription.value = flowDescription.value;
  editRespectBusinessHours.value = respectBusinessHours.value;
  editBusinessHoursStart.value = businessHoursStart.value;
  editBusinessHoursEnd.value = businessHoursEnd.value;
  showEditNameModal.value = true;
};

const applyEditName = () => {
  flowName.value = editName.value.trim();
  flowDescription.value = editDescription.value.trim();
  respectBusinessHours.value = editRespectBusinessHours.value;
  businessHoursStart.value = editBusinessHoursStart.value;
  businessHoursEnd.value = editBusinessHoursEnd.value;
  showEditNameModal.value = false;
};

const openSaveModal = () => {
  if (!flowName.value.trim()) {
    useAlert(t('FLOW_BUILDER.EDITOR.NAME_PLACEHOLDER'));
    return;
  }
  showSaveModal.value = true;
};

const saveFlow = async (status = 'draft') => {
  showSaveModal.value = false;
  saving.value = true;

  const payload = {
    name: flowName.value,
    description: flowDescription.value,
    respect_business_hours: respectBusinessHours.value,
    business_hours_start: businessHoursStart.value,
    business_hours_end: businessHoursEnd.value,
    flow_definition: {
      nodes: cleanNodes(nodes.value),
      edges: cleanEdges(edges.value),
      viewport: { x: 0, y: 0, zoom: 1 },
    },
    status,
  };

  try {
    if (isEditMode.value) {
      await FlowsAPI.update(flowId.value, payload);
      useAlert(t('FLOW_BUILDER.EDITOR.UPDATE_SUCCESS'));
    } else {
      const response = await FlowsAPI.create(payload);
      flowId.value = response.data.id;
      useAlert(t('FLOW_BUILDER.EDITOR.CREATE_SUCCESS'));
    }
    flowStatus.value = status;
    takeSnapshot();
  } catch (error) {
    useAlert(t('FLOW_BUILDER.EDITOR.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const loadFlow = async () => {
  if (!route.params.flowId) return;

  loading.value = true;
  flowId.value = route.params.flowId;
  try {
    const response = await FlowsAPI.show(flowId.value);
    const flow = response.data;
    flowName.value = flow.name || '';
    flowDescription.value = flow.description || '';
    flowStatus.value = flow.status || 'draft';
    respectBusinessHours.value = Boolean(flow.respect_business_hours);
    businessHoursStart.value = flow.business_hours_start || '09:00';
    businessHoursEnd.value = flow.business_hours_end || '18:00';

    if (flow.flow_definition && flow.flow_definition.nodes) {
      nodes.value = cleanNodes(flow.flow_definition.nodes);
      edges.value = cleanEdges(flow.flow_definition.edges);
    }

    takeSnapshot();
  } catch (error) {
    useAlert(t('FLOW_BUILDER.EDITOR.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const allowNavigation = ref(false);

const goBack = () => {
  router.push({
    name: 'flow_builder_list',
    params: { accountId: accountId.value },
  });
};

const confirmExit = () => {
  showExitConfirmModal.value = false;
  allowNavigation.value = true;
  router.push({
    name: 'flow_builder_list',
    params: { accountId: accountId.value },
  });
};

const saveAndExit = async () => {
  if (!flowName.value.trim()) return;
  await saveFlow(flowStatus.value === 'active' ? 'active' : 'draft');
  showExitConfirmModal.value = false;
  allowNavigation.value = true;
  router.push({
    name: 'flow_builder_list',
    params: { accountId: accountId.value },
  });
};

onBeforeRouteLeave(() => {
  if (allowNavigation.value) return true;
  if (hasUnsavedChanges.value) {
    showExitConfirmModal.value = true;
    return false;
  }
  return true;
});

onMounted(() => {
  store.dispatch('inboxes/get');
  if (route.params.flowId) {
    loadFlow();
  } else {
    const triggerNode = {
      id: 'trigger_1',
      type: 'trigger',
      position: { x: 250, y: 50 },
      data: { ...defaultNodeData.trigger },
    };
    nodes.value = [triggerNode];
  }
});
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <!-- Toolbar -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak dark:border-n-strong bg-n-surface-1"
    >
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <div class="flex items-center gap-1.5 min-w-0">
          <button
            class="text-sm font-medium text-n-slate-11 hover:text-n-blue-10 transition-colors shrink-0"
            @click="goBack"
          >
            {{ t('FLOW_BUILDER.LIST.TITLE') }}
          </button>
          <span class="text-n-slate-6 dark:text-n-slate-8 shrink-0">/</span>
          <span
            class="text-sm font-medium text-n-slate-12 truncate"
            :class="{ 'text-n-slate-8 italic': !flowName }"
          >
            {{ flowName || t('FLOW_BUILDER.EDITOR.NAME_PLACEHOLDER') }}
          </span>
          <span v-if="flowDescription" class="text-xs text-n-slate-11 truncate">
            — {{ flowDescription }}
          </span>
        </div>
        <button
          class="shrink-0 p-1 rounded-md text-n-slate-9 hover:text-n-blue-10 hover:bg-n-alpha-2 transition-colors"
          @click="openEditNameModal"
        >
          <span class="i-lucide-pencil text-xs" />
        </button>
        <span
          class="shrink-0 px-2 py-0.5 text-[10px] font-medium rounded-full"
          :class="statusBadge.class"
        >
          {{ statusBadge.label }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">
          {{ nodes.length }} {{ t('FLOW_BUILDER.EDITOR.NODES') }}
        </span>
        <Button
          solid
          blue
          size="sm"
          :label="
            saving
              ? t('FLOW_BUILDER.EDITOR.SAVING')
              : t('FLOW_BUILDER.EDITOR.SAVE')
          "
          :disabled="saving"
          @click="openSaveModal"
        />
      </div>
    </div>

    <!-- Main content: Editor (left) + Canvas -->
    <div class="flex flex-1 overflow-hidden relative">
      <!-- Left Panel: Node Editor -->
      <FlowNodeEditor
        v-if="selectedNode"
        :node="selectedNode"
        :inboxes="inboxes"
        @update="updateNodeData"
        @delete="deleteNode"
        @close="selectedNode = null"
      />

      <!-- Canvas: Vue Flow -->
      <FlowCanvas
        v-model:nodes="nodes"
        v-model:edges="edges"
        :node-types="nodeTypes"
        @drop="onCanvasDrop"
        @connect="onCanvasConnect"
        @node-click="onCanvasNodeClick"
        @pane-click="onCanvasPaneClick"
        @edge-click="onCanvasEdgeClick"
        @node-drag-stop="onCanvasNodeDragStop"
      />

      <!-- Floating + button (top-right) -->
      <button
        class="absolute right-4 top-4 z-20 w-10 h-10 rounded-full bg-n-brand text-white shadow-lg flex items-center justify-center hover:bg-n-brand/90 transition-all"
        :class="{ 'rotate-45': showSidebar }"
        @click="showSidebar = !showSidebar"
      >
        <span class="i-lucide-plus text-lg" />
      </button>

      <!-- Floating popup: Node palette -->
      <Transition name="slide-fade">
        <div
          v-if="showSidebar"
          class="absolute right-4 top-16 z-20 rounded-xl border border-n-weak dark:border-n-strong bg-n-surface-1 shadow-2xl p-3 w-72"
        >
          <FlowSidebar />
        </div>
      </Transition>
    </div>

    <!-- Exit Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="showExitConfirmModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        @click.self="showExitConfirmModal = false"
      >
        <div
          class="bg-white dark:bg-n-slate-1 rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4"
        >
          <div class="flex items-center gap-2 mb-4">
            <span class="i-lucide-alert-triangle text-lg text-n-amber-10" />
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('FLOW_BUILDER.EDITOR.EXIT_CONFIRM.TITLE') }}
            </h3>
          </div>
          <p class="text-sm text-n-slate-11 mb-5">
            {{ t('FLOW_BUILDER.EDITOR.EXIT_CONFIRM.DESCRIPTION') }}
          </p>
          <div class="flex flex-col gap-2">
            <Button
              solid
              blue
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.EXIT_CONFIRM.SAVE')"
              :disabled="!flowName.trim() || saving"
              class="w-full"
              @click="saveAndExit"
            />
            <Button
              solid
              ruby
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.EXIT_CONFIRM.CONFIRM')"
              class="w-full"
              @click="confirmExit"
            />
            <Button
              variant="faded"
              color="slate"
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.EXIT_CONFIRM.CANCEL')"
              class="w-full"
              @click="showExitConfirmModal = false"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Edit Name Modal -->
    <Teleport to="body">
      <div
        v-if="showEditNameModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        @click.self="showEditNameModal = false"
      >
        <div
          class="bg-white dark:bg-n-slate-1 rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4"
        >
          <div class="flex items-center gap-2 mb-4">
            <span class="i-lucide-pencil text-lg text-n-blue-10" />
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('FLOW_BUILDER.EDITOR.EDIT_NAME_MODAL.TITLE') }}
            </h3>
          </div>
          <div class="flex flex-col gap-3 mb-5">
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('FLOW_BUILDER.EDITOR.NAME_PLACEHOLDER') }}
              </label>
              <input
                v-model="editName"
                type="text"
                :placeholder="t('FLOW_BUILDER.EDITOR.NAME_PLACEHOLDER')"
                class="px-3 py-2 text-sm bg-transparent border border-n-weak dark:border-n-strong rounded-lg outline-none focus:border-n-blue-5 text-n-slate-12"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER') }}
              </label>
              <input
                v-model="editDescription"
                type="text"
                :placeholder="t('FLOW_BUILDER.EDITOR.DESCRIPTION_PLACEHOLDER')"
                class="px-3 py-2 text-sm bg-transparent border border-n-weak dark:border-n-strong rounded-lg outline-none focus:border-n-blue-5 text-n-slate-11"
              />
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input
                v-model="editRespectBusinessHours"
                type="checkbox"
                class="rounded border-n-weak text-n-blue-9 focus:ring-n-blue-5"
              />
              {{ t('FLOW_BUILDER.EDITOR.BUSINESS_HOURS.ENABLED') }}
            </label>
            <div v-if="editRespectBusinessHours" class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-n-slate-11">
                  {{ t('FLOW_BUILDER.EDITOR.BUSINESS_HOURS.START') }}
                </label>
                <input
                  v-model="editBusinessHoursStart"
                  type="time"
                  class="px-3 py-2 text-sm bg-transparent border border-n-weak dark:border-n-strong rounded-lg outline-none focus:border-n-blue-5 text-n-slate-12"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-n-slate-11">
                  {{ t('FLOW_BUILDER.EDITOR.BUSINESS_HOURS.END') }}
                </label>
                <input
                  v-model="editBusinessHoursEnd"
                  type="time"
                  class="px-3 py-2 text-sm bg-transparent border border-n-weak dark:border-n-strong rounded-lg outline-none focus:border-n-blue-5 text-n-slate-12"
                />
              </div>
            </div>
            <p v-if="editRespectBusinessHours" class="text-xs text-n-slate-10">
              {{ t('FLOW_BUILDER.EDITOR.BUSINESS_HOURS.DESCRIPTION') }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.EDIT_NAME_MODAL.CANCEL')"
              class="w-full"
              @click="showEditNameModal = false"
            />
            <Button
              solid
              blue
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.EDIT_NAME_MODAL.SAVE')"
              class="w-full"
              @click="applyEditName"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Save Modal -->
    <Teleport to="body">
      <div
        v-if="showSaveModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        @click.self="showSaveModal = false"
      >
        <div
          class="bg-white dark:bg-n-slate-1 rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4"
        >
          <div class="flex items-center gap-2 mb-4">
            <span class="i-lucide-save text-lg text-n-blue-10" />
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('FLOW_BUILDER.EDITOR.SAVE_MODAL.TITLE') }}
            </h3>
          </div>
          <p class="text-sm text-n-slate-11 mb-5">
            {{ t('FLOW_BUILDER.EDITOR.SAVE_MODAL.DESCRIPTION') }}
          </p>
          <div class="flex flex-col gap-2">
            <Button
              solid
              blue
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.SAVE_MODAL.SAVE_ONLY')"
              icon="i-lucide-save"
              :disabled="saving"
              class="w-full"
              @click="saveFlow('draft')"
            />
            <Button
              solid
              teal
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.SAVE_MODAL.SAVE_AND_ACTIVATE')"
              icon="i-lucide-zap"
              :disabled="saving"
              class="w-full"
              @click="saveFlow('active')"
            />
            <Button
              variant="faded"
              color="slate"
              size="sm"
              :label="t('FLOW_BUILDER.EDITOR.SAVE_MODAL.CANCEL')"
              class="w-full"
              @click="showSaveModal = false"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
